/**
 * The shared primitives. Three of them carry design rules that the rest of the
 * app is not allowed to reimplement:
 *
 *   StatusBadge  colour AND shape, unknown always hatched and always spelled out
 *   EmptyState   a title plus what would put something here
 *   ErrorState   what did not happen / why / what you can do -- in whose
 *                words, see `DeveloperView` below
 */

import { createContext, useContext, type ReactNode } from "react";
import type { Tone } from "../lib/format";
import { TONE_GLYPH, labelForState } from "../lib/format";
import { ApiError } from "../lib/api";
import { Icon } from "../components/Icon";
import { LightMotion } from "../components/LightMotion";

/**
 * Is the developer view on -- may a server's own sentences be drawn?
 *
 * An `ApiError` marked `machine` carries three sentences written by something
 * that does not know who is looking: they name panels, the core, socket files
 * and exception classes (`workbench/server/app.py:462` puts 面板 and 核 in one
 * line). Which page they land on is decided by whoever calls `ErrorState`, and
 * Owner pages call it -- a 404 from `/api/connections/llm_providers` printed
 * 「…设置面板。」 on 准备与内容方向 (the report's D297). So it is decided in one
 * place instead of at thirty call sites: with the view off an owner reads what
 * is true of the page in front of them and how to get past it; with it on the
 * three lines are there, which is what that view is for. A failure this shell
 * wrote itself is a sentence somebody wrote for a person, and is shown either
 * way.
 *
 * Off by default, because a page that forgets to say which one it is is an
 * Owner page until proven otherwise. `App` provides the switch's value.
 */
export const DeveloperView = createContext(false);

export function StatusBadge({
  tone,
  label,
  title,
}: {
  tone: Tone;
  label?: string;
  title?: string;
}) {
  const text = label ?? labelForState(tone);
  return (
    <span className={`badge badge-${tone}`} title={title} data-tone={tone}>
      <span className="badge-glyph" aria-hidden="true">
        {TONE_GLYPH[tone]}
      </span>
      {text}
    </span>
  );
}

export function StateBadge({ state }: { state: unknown }) {
  const tone: Tone =
    typeof state === "string" && state
      ? ((
          {
            replied: "good",
            silent: "good",
            sent: "good",
            succeeded: "good",
            settled: "good",
            held: "warning",
            failed: "critical",
            unknown: "unknown",
          } as Record<string, Tone>
        )[state] ?? "neutral")
      : "unknown";
  return (
    <StatusBadge
      tone={tone}
      label={labelForState(state)}
      title={typeof state === "string" ? state : "核没有给出状态"}
    />
  );
}

export function EmptyState({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="state-block state-empty" role="status">
      <LightMotion state="waiting" />
      <p className="state-title">{title}</p>
      <p className="state-hint">{hint}</p>
    </div>
  );
}

export function LoadingState({ label = "读取中" }: { label?: string }) {
  return (
    <div className="state-block state-loading" role="status" aria-live="polite" aria-busy="true">
      <LightMotion state="working" />
      <p className="state-title">{label}…</p>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const developer = useContext(DeveloperView);
  const api = ApiError.fromUnknown(error);
  /** Somebody else's words, and nobody here to read them. */
  const quiet = api.machine && !developer;
  return (
    <div className="state-block state-error" role="alert">
      <p className="state-title">
        <Icon name="alert" size={24} />
        {quiet ? "这一块暂时没有读到" : api.title}
      </p>
      {quiet ? (
        <p className="state-detail">
          页面上其余的内容照常。可以再试一次；一直读不到就回到对话里问它，它知道自己缺什么。
        </p>
      ) : (
        <>
          <p className="state-detail">{api.detail}</p>
          {api.hint ? <p className="state-hint">{api.hint}</p> : null}
        </>
      )}
      {onRetry ? (
        <button type="button" className="btn" onClick={onRetry}>
          再试一次
        </button>
      ) : null}
    </div>
  );
}

export function Section({
  title,
  meta,
  actions,
  children,
}: {
  title: string;
  meta?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="section">
      <header className="section-head">
        <h2 className="section-title">{title}</h2>
        {meta ? <div className="section-meta micro">{meta}</div> : null}
        {actions ? <div className="section-actions">{actions}</div> : null}
      </header>
      {children}
    </section>
  );
}

/** Long tables scroll inside this. The page itself never scrolls sideways. */
export function Scroller({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="scroller" tabIndex={0} role="region" aria-label={label}>
      {children}
    </div>
  );
}

/**
 * Text in a cell: one line, truncated, with the whole value in `title`.
 *
 * A `max-width` on a table cell is only a suggestion: under `table-layout: auto`
 * the browser sizes columns from content, and one long string still stretches
 * the column and runs under the ones to its right.  What actually holds is a
 * max-width on a block element *inside* the cell -- so truncation needs an
 * element of its own and cannot hang off the td.
 *
 * Truncation is a display concern, not a data one: `title` carries the whole
 * value, and so does the row's expanded detail.  Nothing may hide behind the
 * ellipsis that cannot be seen anywhere else.
 */
export function Clip({
  text,
  children,
  width,
}: {
  /** The whole value.  It goes into `title`, so the ellipsis hides nothing. */
  text: string;
  children?: ReactNode;
  /** This column's ceiling, 46ch by default. */
  width?: string;
}) {
  return (
    <span
      className="cell-clip"
      title={text || undefined}
      style={width ? { maxWidth: width } : undefined}
    >
      {children ?? text}
    </span>
  );
}
